import React, { useMemo, useCallback } from 'react';
import WarningIcon from '../ui/WarningIcon';
import CableColorRect from '../ui/CableColorRect';
import { Icons } from '../icons/Icons'; // Assuming Icons is exported as a named export
import { getCableWidthForTessera, formatSezione, getCableDisplayName } from '../../utils/cableLogic';

const CableTessera = React.memo(({
  cable,
  displaySiglatura,
  onCableClick,
  onEditCable,
  onEditMultipolarCable,
  onDeleteCable,
  onDeleteMultipolarCable,
  showSiglatura = true,
  clickable = true,
  settings = {},
  isDragEnabled = false,
  validateCable, // This is a prop
  isDragging = false,
  isDragOver = false,
  hasIssues = false,
  isHovered = false,
  editingIndex = -1,
  editingMultipolarIndex = -1,
  showDeleteButton = false,
  onToggleDelete,
  isSelectionModeEnabled = false,
  selectedCables = new Set(),
  onToggleSelection,
  availableSheaths = [],
  hideSheathBadge = false
}) => {
  const isMultipolar = Boolean(cable.poli && Array.isArray(cable.poli));
  const representativeData = isMultipolar ? cable.poli[0] : cable;
  
  // Memorizza la validazione solo se il cavo cambia
  const validation = useMemo(() => validateCable(cable), [cable, validateCable]);
  const actualHasIssues = hasIssues || !validation.isValid;
  const isBeingEdited = isMultipolar ? editingMultipolarIndex === cable.originalIndex : editingIndex === cable.originalIndex;
  const shouldShowDelete = Boolean(showDeleteButton || isBeingEdited);

  // Memorizza le classi CSS per evitare ricalcoli
  const containerClasses = useMemo(() => {
    const baseClasses = [
      'border-2', 'rounded-lg', 'transition-all', 'duration-200'
    ];
    
    if (isDragEnabled) baseClasses.push('cursor-move');
    else if (clickable) baseClasses.push('cursor-pointer');
    
    if (actualHasIssues) {
      baseClasses.push('bg-amber-50', 'border-amber-300', 'hover:bg-amber-100', 'hover:border-amber-400');
    } else if (isHovered) {
      baseClasses.push('bg-blue-50', 'border-blue-300');
    } else {
      // Differenzia lo sfondo per multipolari
      if (isMultipolar) {
        baseClasses.push('bg-gradient-to-r', 'from-purple-50', 'to-indigo-50', 'border-purple-200', 'hover:border-purple-300');
      } else {
        baseClasses.push('bg-gray-50', 'border-gray-200', 'hover:bg-gray-100');
      }
    }
    
    if (isDragEnabled && !actualHasIssues) {
      const hoverColor = isMultipolar ? 'purple' : 'blue';
      baseClasses.push(`hover:bg-${hoverColor}-50`, `hover:border-${hoverColor}-300`);
    }
    
    if (isDragging) {
      baseClasses.push('opacity-50', 'transform', 'scale-95', 'border-blue-400');
    }
    
    if (isDragOver) {
      baseClasses.push('border-blue-500', 'bg-blue-100');
    }
    
    return baseClasses.join(' ');
  }, [isDragEnabled, clickable, actualHasIssues, isHovered, isDragging, isDragOver, isMultipolar]);

  // Memorizza il calcolo della larghezza del cavo
  const cableWidth = useMemo(() => 
    getCableWidthForTessera(representativeData.sezione, settings.useAWG), 
    [representativeData.sezione, settings.useAWG]
  );

  // Handler click memorizzato
  const handleClick = useCallback(() => {
    if (clickable && onCableClick) {
      const title = isMultipolar 
        ? cable.nomeCavo || `Multipolare ${cable.poli.length}P` 
        : displaySiglatura;
      onCableClick(title, cable.originalIndex);
    }
  }, [clickable, onCableClick, displaySiglatura, cable.originalIndex, isMultipolar, cable.nomeCavo, cable.poli]);

  // Handler edit memorizzato
  const handleEdit = useCallback((e) => {
    e.stopPropagation();
    if (onToggleDelete) onToggleDelete();
    
    if (cable.poli && Array.isArray(cable.poli)) {
      onEditMultipolarCable?.(cable.originalIndex);
    } else {
      onEditCable(cable.originalIndex);
    }
  }, [onToggleDelete, onEditCable, onEditMultipolarCable, cable]);

  // Handler delete memorizzato
  const handleDelete = useCallback((e) => {
    e.stopPropagation();
    
    if (cable.poli && Array.isArray(cable.poli)) {
      onDeleteMultipolarCable(cable.originalIndex);
    } else {
      onDeleteCable(cable.originalIndex);
    }
  }, [onDeleteCable, onDeleteMultipolarCable, cable]);

  // ==================== RENDERING CAVI MULTIPOLARI ====================
  if (isMultipolar) {
    return (
      <div className={`${containerClasses} p-4 relative`} onClick={handleClick}>
        {/* HEADER MULTIPOLARE */}
        <div className={`flex items-center justify-between mb-4 ${
          isDragEnabled ? 'ml-8' : ''
        }`}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Checkbox per selezione - sempre primo elemento se attivo */}
            {isSelectionModeEnabled && (
              <label 
                className="flex items-center cursor-pointer flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelection && onToggleSelection(cable.originalIndex);
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedCables.has(cable.originalIndex)}
                  onChange={() => onToggleSelection && onToggleSelection(cable.originalIndex)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-5 h-5 text-green-600 border-2 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                />
              </label>
            )}

            {/* WARNING ICON - secondo elemento se attivo */}
            {!isDragEnabled && actualHasIssues && (
              <div className="flex-shrink-0">
                <WarningIcon
                  issues={validation.issues}
                  className="flex-shrink-0"
                />
              </div>
            )}

            {/* Badge numero - terzo elemento */}
            <span className={`px-2 py-0.5 rounded text-sm font-medium flex-shrink-0 ${
              actualHasIssues ? 'bg-amber-200 text-amber-800' : 'bg-blue-100 text-blue-800'
            }`}>
              #{cable.originalIndex + 1}
            </span>

            {/* Info principale */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {cable.nomeCavo || 'Cavo Multipolare'}
                </span>
                <span className="text-sm text-gray-500 flex-shrink-0">
                  {(() => {
                    const sezioni = [...new Set(cable.poli.map(p => p.sezione))];
                    const hasUniformSection = sezioni.length === 1;
                    
                    return hasUniformSection 
                      ? `${formatSezione(sezioni[0], settings.useAWG)} • ${cable.lunghezza}mm`
                      : `${cable.lunghezza}mm`;
                  })()}
                </span>
              </div>
            </div>
          </div>

          {/* Badge numero poli + Azioni */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
              Multipolare
            </div>

            {/* Azioni */}
            <div className="flex items-center gap-1 ml-2">
              {!shouldShowDelete ? (
                <button
                  onClick={handleEdit}
                  disabled={isDragEnabled || isSelectionModeEnabled || ((editingIndex >= 0 || editingMultipolarIndex >= 0) && !isBeingEdited)}
                  className={`p-2 rounded transition-colors ${
                    isDragEnabled || isSelectionModeEnabled || ((editingIndex >= 0 || editingMultipolarIndex >= 0) && !isBeingEdited)
                      ? 'text-gray-300 cursor-not-allowed'
                      : actualHasIssues
                        ? 'text-amber-600 hover:bg-amber-100'
                        : 'text-purple-600 hover:bg-purple-100'
                  }`}
                  title={
                    isSelectionModeEnabled
                      ? "Disabilita la modalità selezione per modificare i cavi"
                      : (editingIndex >= 0 || editingMultipolarIndex >= 0) && !isBeingEdited 
                        ? "Termina la modifica in corso prima di modificare questo cavo"
                        : "Modifica cavo multipolare"
                  }
                >
                  <Icons.Edit3 className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleDelete}
                  disabled={isDragEnabled}
                  className={`p-2 rounded transition-colors ${
                    isDragEnabled ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'
                  }`}
                  title="Cancella cavo multipolare definitivamente"
                >
                  <Icons.Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* GRIGLIA POLI - resto del codice rimane uguale */}
        <div className="space-y-2">
          {cable.poli.map((polo, index) => {
            const poloWidth = getCableWidthForTessera(polo.sezione, settings.useAWG);
            
            return (
              <div 
                key={polo.id || index} 
                className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow"
              >
                {/* Resto del rendering dei poli rimane identico all'originale */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Numero polo */}
                    <span className="text-xs font-bold text-gray-600 bg-gray-100 px-2 py-1 rounded flex-shrink-0 min-w-[28px] text-center">
                      P{index + 1}
                    </span>
                    
                    {/* Cavo colorato */}
                    <CableColorRect
                      color={polo.colore}
                      width={poloWidth}
                      height="45px"
                      borderRadius="9999px"
                      className="transition-all duration-200 flex-shrink-0"
                    />

                    {/* Informazioni del polo */}
                    <div className="flex-1 min-w-0">
                      {/* Lato A */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold min-w-[24px] text-center">
                          A
                        </div>
                        {!settings.marcaturePerCollegamento && (
                          <>
                            <span className="text-sm font-medium">
                              {(() => {
                                const isEarthWire = polo.colore === 'GI/VE' || polo.colore === 'YE/GN';
                                if (isEarthWire) {
                                  return settings.lingua === 'it' ? 'TERRA' : 'EARTH';
                                }
                                return polo.siglaturaA || '';
                              })()}
                            </span>
                          </>
                        )}
                        <span className="text-sm text-gray-500 flex-shrink-0">
                          {polo.colore}
                        </span>
                        <span className="text-gray-400">•</span>
                        {(() => {
                          const sezioni = [...new Set(cable.poli.map(p => p.sezione))];
                          const hasUniformSection = sezioni.length === 1;
                          
                          return !hasUniformSection ? (
                            <>
                              <span className="text-sm text-gray-500 flex-shrink-0">
                                {formatSezione(polo.sezione, settings.useAWG)}
                              </span>
                              <span className="text-gray-400">•</span>
                            </>
                          ) : null;
                        })()}
                        <span className="text-sm text-gray-500">{polo.lunghezzaA || 0}mm</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-sm font-medium text-gray-600">{polo.terminaleA}</span>
                        <span className="text-gray-400">|</span>
                        <span className="text-sm font-medium text-gray-600">{polo.collegamentoA || '—'}</span>
                      </div>

                      <div className="border-t border-gray-100 my-2"></div>
                      {/* Lato B */}
                      <div className="flex items-center gap-2">
                        <div className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-bold min-w-[24px] text-center">
                          B
                        </div>
                        {!settings.marcaturePerCollegamento && (
                          <>
                            <span className="text-sm font-medium">
                              {(() => {
                                const isEarthWire = polo.colore === 'GI/VE' || polo.colore === 'YE/GN';
                                if (isEarthWire) {
                                  return settings.lingua === 'it' ? 'TERRA' : 'EARTH';
                                }
                                return polo.siglaturaB || '';
                              })()}
                            </span>
                          </>
                        )}
                        <span className="text-sm text-gray-500 flex-shrink-0">
                          {polo.colore}
                        </span>
                        <span className="text-gray-400">•</span>
                        {(() => {
                          const sezioni = [...new Set(cable.poli.map(p => p.sezione))];
                          const hasUniformSection = sezioni.length === 1;
                          
                          return !hasUniformSection ? (
                            <>
                              <span className="text-sm text-gray-500 flex-shrink-0">
                                {formatSezione(polo.sezione, settings.useAWG)}
                              </span>
                              <span className="text-gray-400">•</span>
                            </>
                          ) : null;
                        })()}
                        <span className="text-sm text-gray-500">{polo.lunghezzaB || 0}mm</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-sm font-medium text-gray-600">{polo.terminaleB}</span>
                        <span className="text-gray-400">|</span>
                        <span className="text-sm font-medium text-gray-600">{polo.collegamentoB || '—'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ==================== RENDERING CAVI STANDARD ==================== 
  return (
    <div className={`${containerClasses} p-3 flex items-center justify-between relative`} onClick={handleClick}>
      <div className={`flex items-center gap-3 flex-1 min-w-0 ${
        isDragEnabled ? 'ml-8' : ''
      }`}>
        {/* Checkbox per selezione - sempre primo elemento se attivo */}
        {isSelectionModeEnabled && (
          <label className="flex items-center cursor-pointer flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selectedCables.has(cable.originalIndex)}
              onChange={() => onToggleSelection && onToggleSelection(cable.originalIndex)}
              className="w-5 h-5 text-green-600 border-2 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
            />
          </label>
        )}

        {/* WARNING ICON - secondo elemento se attivo */}
        {!isDragEnabled && actualHasIssues && (
          <div className="flex-shrink-0">
            <WarningIcon
              issues={validation.issues}
              className="flex-shrink-0"
            />
          </div>
        )}

        {/* Badge numero - terzo elemento */}
        <span className={`px-2 py-0.5 rounded text-sm font-medium flex-shrink-0 ${
          actualHasIssues ? 'bg-amber-200 text-amber-800' : 'bg-blue-100 text-blue-800'
        }`}>
          #{cable.originalIndex + 1}
        </span>

        {/* Anteprima sezione cavo */}
        <CableColorRect
          color={representativeData.colore}
          width={cableWidth}
          height="32px"
          borderRadius="9999px"
          className="transition-all duration-200 flex-shrink-0"
        />

        {/* Informazioni cavo */}
        <div className="flex-1 min-w-0">
          {/* Prima riga: Siglatura + dettagli tecnici */}
          <div className="flex items-center gap-2">
            {showSiglatura && (
              <span className="font-medium">
                {settings.marcaturePerCollegamento
                  ? getCableDisplayName(cable, settings.lingua, settings)
                  : (displaySiglatura || representativeData.siglatura)
                }
              </span>
            )}
            <span className="text-sm text-gray-500 flex-shrink-0">
              {representativeData.colore} • {formatSezione(representativeData.sezione, settings.useAWG)} • {cable.lunghezza}mm
            </span>
          </div>

          <div className="text-sm text-gray-600 mt-1 truncate">
            <span className="font-medium">{representativeData.terminaleA}</span>
            <span className="mx-1">→</span>
            <span className="font-medium">{representativeData.terminaleB}</span>

            {!settings.marcaturePerCollegamento && (
              <>
                <span className="mx-2 text-gray-400">|</span>
                <span className="font-medium text-gray-700">{representativeData.collegamentoA || '—'}</span>
                <span className="mx-1">→</span>
                <span className="font-medium text-gray-700">{representativeData.collegamentoB || '—'}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Badge Guaina - allineato a destra (nascosto se già in un gruppo guaina) */}
      {!hideSheathBadge && cable.guaina && (() => {
        const sheath = availableSheaths.find(s => s.id === cable.guaina);
        return sheath ? (
          <div className="flex-shrink-0 ml-2">
            <span className="px-2 py-0.5 bg-sky-100 text-sky-800 rounded text-xs font-medium whitespace-nowrap">
              {sheath.label}
            </span>
          </div>
        ) : null;
      })()}
      
      {/* Azioni */}
      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
        {!shouldShowDelete ? (
          <button
            onClick={handleEdit}
            disabled={isDragEnabled || isSelectionModeEnabled || ((editingIndex >= 0 || editingMultipolarIndex >= 0) && !isBeingEdited)}
            className={`p-2 rounded transition-colors ${
              isDragEnabled || isSelectionModeEnabled || ((editingIndex >= 0 || editingMultipolarIndex >= 0) && !isBeingEdited)
                ? 'text-gray-300 cursor-not-allowed'
                : actualHasIssues
                  ? 'text-amber-600 hover:bg-amber-100'
                  : 'text-blue-600 hover:bg-blue-50'
            }`}
            title={
              isSelectionModeEnabled
                ? "Disabilita la modalità selezione per modificare i cavi"
                : (editingIndex >= 0 || editingMultipolarIndex >= 0) && !isBeingEdited 
                  ? "Termina la modifica in corso prima di modificare questo cavo"
                  : "Modifica cavo"
            }
          >
            <Icons.Edit3 className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleDelete}
            disabled={isDragEnabled}
            className={`p-2 rounded transition-colors ${
              isDragEnabled ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'
            }`}
            title="Cancella cavo definitivamente"
          >
            <Icons.Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // ✅ Memoization aggiornata per entrambe le strutture
  const prevIsMultipolar = prevProps.cable.poli && Array.isArray(prevProps.cable.poli);
  const nextIsMultipolar = nextProps.cable.poli && Array.isArray(nextProps.cable.poli);
  
  if (prevIsMultipolar !== nextIsMultipolar) {
    return false; // Tipo cambiato, re-render
  }
  
  if (nextIsMultipolar) {
    // Confronto per cavi multipolari
    return (
      prevProps.cable.originalIndex === nextProps.cable.originalIndex &&
      prevProps.cable.nomeCavo === nextProps.cable.nomeCavo &&
      prevProps.cable.lunghezza === nextProps.cable.lunghezza &&
      prevProps.cable.gruppo === nextProps.cable.gruppo &&
      JSON.stringify(prevProps.cable.poli) === JSON.stringify(nextProps.cable.poli) &&
      prevProps.isDragEnabled === nextProps.isDragEnabled &&
      prevProps.isDragging === nextProps.isDragging &&
      prevProps.isDragOver === nextProps.isDragOver &&
      prevProps.hasIssues === nextProps.hasIssues &&
      prevProps.editingIndex === nextProps.editingIndex &&
      prevProps.editingMultipolarIndex === nextProps.editingMultipolarIndex &&
      prevProps.showDeleteButton === nextProps.showDeleteButton &&
      prevProps.settings.marcaturePerCollegamento === nextProps.settings.marcaturePerCollegamento &&
      prevProps.settings.useAWG === nextProps.settings.useAWG
    );
  } else {
    // Confronto per cavi standard (logica esistente)
    return (
      prevProps.cable.originalIndex === nextProps.cable.originalIndex &&
      prevProps.cable.siglatura === nextProps.cable.siglatura &&
      prevProps.cable.colore === nextProps.cable.colore &&
      prevProps.cable.sezione === nextProps.cable.sezione &&
      prevProps.cable.lunghezza === nextProps.cable.lunghezza &&
      prevProps.cable.terminaleA === nextProps.cable.terminaleA &&
      prevProps.cable.terminaleB === nextProps.cable.terminaleB &&
      prevProps.cable.collegamentoA === nextProps.cable.collegamentoA &&
      prevProps.cable.collegamentoB === nextProps.cable.collegamentoB &&
      prevProps.displaySiglatura === nextProps.displaySiglatura &&
      prevProps.showSiglatura === nextProps.showSiglatura &&
      prevProps.clickable === nextProps.clickable &&
      prevProps.isDragEnabled === nextProps.isDragEnabled &&
      prevProps.isDragging === nextProps.isDragging &&
      prevProps.isDragOver === nextProps.isDragOver &&
      prevProps.hasIssues === nextProps.hasIssues &&
      prevProps.isHovered === nextProps.isHovered &&
      prevProps.editingIndex === nextProps.editingIndex &&
      prevProps.editingMultipolarIndex === nextProps.editingMultipolarIndex &&
      prevProps.showDeleteButton === nextProps.showDeleteButton &&
      prevProps.isSelectionModeEnabled === nextProps.isSelectionModeEnabled &&
      (prevProps.selectedCables?.size || 0) === (nextProps.selectedCables?.size || 0) &&
      (!prevProps.selectedCables || !nextProps.selectedCables || 
       [...prevProps.selectedCables].every(item => nextProps.selectedCables.has(item))) &&
      prevProps.onToggleSelection === nextProps.onToggleSelection &&
      prevProps.settings.marcaturePerCollegamento === nextProps.settings.marcaturePerCollegamento &&
      prevProps.settings.lingua === nextProps.settings.lingua
    );
  }
});

export default CableTessera;